import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid';
import {
  Box,
  Button,
  Dialog,
  Checkbox,
  Typography,
  DialogTitle,
  DialogContent,
  FormControlLabel,
} from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';

import { Form, Field } from 'src/components/hook-form';

export const WEBHOOK_EVENTS = [
  { key: 'ticket.add', label: 'Ticket Created' },
  { key: 'ticket.update', label: 'Ticket Updated' },
  { key: 'ticket.delete', label: 'Ticket Deleted' },

  { key: 'contact.add', label: 'Contact Created' },
  { key: 'contact.update', label: 'Contact Updated' },
  { key: 'contact.delete', label: 'Contact Deleted' },

  { key: 'company.add', label: 'Company Created' },
  { key: 'company.update', label: 'Company Updated' },
  { key: 'company.delete', label: 'Company Deleted' },
];

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUser?: any;
  handleSubmitData: (data: any) => void;
};

export function WebhookForm({ open, onClose, selectedUser, handleSubmitData }: Props) {
  const { t } = useTranslate('translation');

  const fldList = [
    { name: 'webhookName', required: true },
    { name: 'webhookUrl', required: true },
    { name: 'events', required: true },
  ];

  const webhookSchema = validationSchema(fldList, t);

  const methods = useForm({
    mode: 'all',
    resolver: zodResolver(webhookSchema),
    defaultValues: {
      webhookName: '',
      webhookUrl: '',
      events: [] as string[],
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (selectedUser) {
      reset({
        webhookName: selectedUser.name || '',
        webhookUrl: selectedUser.url || '',
        events: selectedUser.events || [],
      });
    } else {
      reset();
    }
  }, [selectedUser, reset]);

  const onSubmit = (data: any) => {
    handleSubmitData({ id: selectedUser?.id || '', ...data });
    onCloseHandler();
  };

  const onCloseHandler = () => {
    reset();
    onClose();
  };

  const selectedEvents = watch('events');

  const handleToggleEvent = (eventKey: string) => {
    const newEvents = selectedEvents.includes(eventKey)
      ? selectedEvents.filter((e) => e !== eventKey)
      : [...selectedEvents, eventKey];
    setValue('events', newEvents);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onCloseHandler}
      sx={{
        '& .MuiPaper-rounded': {
          p: 0.5,
          boxShadow: '0px 0px 30px rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <Box>
        <DialogTitle sx={(theme) => ({ p: theme.spacing(3, 3, 2, 3) })}>
          {selectedUser ? t('EDIT_WEBHOOK') : t('ADD_NEW_WEBHOOK')}
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'nonet' }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('WEBHOOK_EVENTS_INFO')}
          </Typography>

          <Form methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mb: 2 }}>
              <Field.Text
                name="webhookName"
                label={t('NAME')}
                placeholder="Example: CRM Webhook"
                required
              />
            </Box>

            <Field.Text
              name="webhookUrl"
              label={t('WEBHOOK_URL')}
              placeholder="Example: https://example.com/api/webhook"
              required
            />

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              {t('EVENTS')}
            </Typography>

            <Grid container spacing={1}>
              {WEBHOOK_EVENTS.map((event) => (
                <Grid item xs={12} sm={6} key={event.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedEvents.includes(event.key)}
                        onChange={() => handleToggleEvent(event.key)}
                      />
                    }
                    label={event.label}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'end', my: 3 }}>
              <Button variant="outlined" color="inherit" onClick={onCloseHandler}>
                {t('CANCEL')}
              </Button>

              <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                {t('SAVE')}
              </Button>
            </Box>
          </Form>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
